/**
 * Spec 217 PR4.b — Route role guard.
 *
 * Wraps a route element so only callers with the allowed roles see
 * the children. Anyone else gets a friendly empty-state with a
 * "go back" link explaining what their role can and can't do.
 *
 * Mirrors the mobile-side gate (`SettingsScreen.tsx` "Sesiones web
 * conectadas" + `Broadcasts` are admin-only there too) so a worker
 * who logs into the web sees the same surface gating.
 *
 * Usage:
 *   <Route path="/broadcasts" element={
 *     <RoleGuard
 *       identity={identity}
 *       allow={['creator', 'admin']}
 *       feature="Avisos del equipo"
 *     >
 *       <Broadcasts identity={identity} />
 *     </RoleGuard>
 *   } />
 *
 * The fallback is rendered inline with its own markup (instead of
 * reusing EmptyState's variant-locked surface) so we don't have to
 * carve a new variant just for the gate. Tokens come from the
 * design system so light + dark stay consistent automatically.
 */
import { Link } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';

import type { WebIdentity } from '../store/keys';

import './RoleGuard.css';

export type Role = WebIdentity['role'];

const ROLE_LABEL: Record<Role, string> = {
  creator: 'Creador',
  admin: 'Administrador',
  member: 'Miembro',
};

interface RoleGuardProps {
  identity: WebIdentity;
  /** Roles that may view the children. */
  allow: ReadonlyArray<Role>;
  /**
   * Display name of the gated feature. Used in the fallback copy
   * ("Avisos del equipo está limitado a admins").
   */
  feature: string;
  children: React.ReactNode;
}

export function RoleGuard({ identity, allow, feature, children }: RoleGuardProps) {
  if (allow.includes(identity.role)) {
    return <>{children}</>;
  }
  return (
    <main
      id="main-content"
      className="role-guard-main"
      tabIndex={-1}
      data-testid="role-guard-fallback"
    >
      {/* sr-only h1 — same a11y pattern as Settings/Stats/WorkList:
          paired layout hides route headers, so axe needs an accessible
          heading anchor for `page-has-heading-one`. */}
      <h1 className="sr-only">Acceso restringido</h1>
      <div className="role-guard-card" role="status" data-testid="role-guard-card">
        <div className="role-guard-icon" aria-hidden="true">
          <ShieldOff size={48} strokeWidth={2} />
        </div>
        <h2 className="role-guard-title">{feature} está limitado a administradores</h2>
        <p className="role-guard-body">
          Tu rol actual es <strong>{ROLE_LABEL[identity.role]}</strong>. Para usar esta sección
          necesitas que un administrador del equipo te promueva. Mientras tanto, sigues teniendo
          acceso a Trabajos y Estadísticas.
        </p>
        <Link to="/" className="role-guard-back" data-testid="role-guard-back">
          Volver a Trabajos
        </Link>
      </div>
    </main>
  );
}
